import { Col, Container, ModalBody, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useGetMeasurementUnitsQuery } from "../../../../../backoffice/apis/measurementApi";
import { useCallback, useMemo} from "react";
import IdItemSelection from "../../../../views/common/IdItemSelection";
import { SelectOption } from "../../../../../helpers/SelectOption";
import DepartmentMeasurements from "../../../../../backoffice/dtos/groups/DepartmentMeasurements";
import DepartmentMeasurementIndexes from "../../../../../backoffice/dtos/groups/DepartmentMeasurementIndexes";
import { Group } from "../../../../../backoffice/dtos/groups/group";

export default function DepartmentsMeasurementUnitsSettings(
    {
        group,
        toggle,
        errors,
        departmentsMeasurements,
        setDepartmentsMeasurements
    }: {
        group: Group;
        toggle: () => void;
        errors: Map<string, string> | undefined;
        departmentsMeasurements: DepartmentMeasurements;
        setDepartmentsMeasurements: (departmentMeasurementsFunc: (departmentMeasurements: DepartmentMeasurements) => DepartmentMeasurements) => void;
    }
) {
    const { t } = useTranslation(['measurement_units', 'common'])

    const {
        data: measurementUnits,
        isLoading: measurementUnitsLoading,
        error: measurementUnitsError
    } = useGetMeasurementUnitsQuery(undefined, { refetchOnMountOrArgChange: true })

    if (measurementUnitsLoading) {
        return (
            <>
                <ModalBody className="d-flex flex-row justify-content-center">
                    <Spinner className="m-2" />
                </ModalBody>
            </>
        );
    } else if (measurementUnitsError || !measurementUnits) {
        return (
            <>
                <ModalBody>
                    Failed to fetch data
                </ModalBody>
            </>
        );
    } else {
        return (
            <InnerDepartmentsMeasurementUnitsSettings
                group={group}
                toggle={toggle}
                errors={errors}
                measurementUnits={measurementUnits}
                groupMeasurementUnits={departmentsMeasurements}
                setGroupMeasurementUnits={setDepartmentsMeasurements}
            />
        )
    }
}

function InnerDepartmentsMeasurementUnitsSettings(
    {
        group,
        toggle,
        errors,
        measurementUnits,
        groupMeasurementUnits,
        setGroupMeasurementUnits
    }: {
        group: Group;
        toggle: () => void;
        errors: Map<string, string> | undefined;
        measurementUnits: string[];
        groupMeasurementUnits: DepartmentMeasurements | null;
        setGroupMeasurementUnits: (departmentMeasurementsFunc: (departmentMeasurements: DepartmentMeasurements) => DepartmentMeasurements) => void;
    }
) {
    const { t } = useTranslation(['measurement_units', 'common'])

    const items = useMemo(() => {
        return measurementUnits.map<SelectOption>((m, i) => ({ value: i + 1, label: m }))
    }, [measurementUnits]);

    const selection = useMemo(() => {
        if (groupMeasurementUnits === null) {
            return new DepartmentMeasurementIndexes(0, 0, 0, 0, 0);
        } else {
            return new DepartmentMeasurementIndexes(
                measurementUnits.findIndex(m => m === groupMeasurementUnits.primaryMeasurementUnit) + 1,
                measurementUnits.findIndex(m => m === groupMeasurementUnits.predictionMeasurementUnit) + 1,
                measurementUnits.findIndex(m => m === groupMeasurementUnits.actualInputMeasurementUnit) + 1,
                measurementUnits.findIndex(m => m === groupMeasurementUnits.calculatedMeasurementUnit) + 1,
                measurementUnits.findIndex(m => m === groupMeasurementUnits.secondaryCalculatedMeasurementUnit) + 1
            );
        }
    }, [groupMeasurementUnits]);

    const setSelection = useCallback((name: string, value: number | undefined) => {
        let valueStr: string | undefined = undefined;
        if (value && value - 1 >= 0 && value - 1 < measurementUnits.length) {
            valueStr = measurementUnits[value - 1];
        }
        setGroupMeasurementUnits(ref => {
            return { ...ref, [name]: valueStr}
        });
    }, [setGroupMeasurementUnits, measurementUnits])

    return (
        <>
            <Container>
                <Row className="gy-1">
                    <Col lg={5} className="d-flex align-items-center justify-content-end">
                        {t('Primary Measurement')}*
                    </Col>
                    <Col>
                        <IdItemSelection id={selection.primaryMeasurementUnit} options={items} setSelection={(code) => setSelection("primaryMeasurementUnit", code ?? 0)} disabled={group.id > 0} />
                        {
                            errors && errors.has("primaryMeasurementUnit") && <span className="text-danger">{errors.get("primaryMeasurementUnit")}</span>
                        }
                    </Col>
                </Row>
                <hr />
                <h4>Product - Prediction</h4>
                <Row className="mt-3">
                    <Col lg={5} className="d-flex align-items-center justify-content-end">
                        {t('Prediction Measurement')}*
                    </Col>
                    <Col>
                        <IdItemSelection id={selection.predictionMeasurementUnit} options={items} setSelection={(code) => setSelection("predictionMeasurementUnit", code ?? 0)} />
                        {
                            errors && errors.has("predictionMeasurementUnit") && <span className="text-danger">{errors.get("predictionMeasurementUnit")}</span>
                        }
                    </Col>
                </Row>
                <hr />
                <h4>Product - Actual</h4>
                <Row >
                    <Col lg={5} className="d-flex align-items-center justify-content-end">
                        {t('Input Measurement')}*
                    </Col>
                    <Col>
                        <IdItemSelection id={selection.actualInputMeasurementUnit} options={items} setSelection={(code) => setSelection("actualInputMeasurementUnit", code ?? 0)} />
                        {
                            errors && errors.has("actualInputMeasurementUnit") && <span className="text-danger">{errors.get("actualInputMeasurementUnit")}</span>
                        }
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col lg={5} className="d-flex align-items-center justify-content-end">
                        {t('Calculated Measurement')}*
                    </Col>
                    <Col>
                        <IdItemSelection id={selection.calculatedMeasurementUnit} options={items} setSelection={(code) => setSelection("calculatedMeasurementUnit", code ?? 0)} />
                        {
                            errors && errors.has("calculatedMeasurementUnit") && <span className="text-danger">{errors.get("calculatedMeasurementUnit")}</span>
                        }
                    </Col>
                </Row>
                {
                    selection.calculatedMeasurementUnit > 0 &&
                        <Row className="mt-2">
                            <Col lg={5} className="d-flex align-items-center justify-content-end">
                                {t('Secondary Calculated Measurement')}
                            </Col>
                            <Col>
                                <IdItemSelection id={selection.secondaryCalculatedMeasurementUnit} options={items} setSelection={(code) => setSelection("secondaryCalculatedMeasurementUnit", code ?? 0)} />
                                {
                                    errors && errors.has("secondaryCalculatedMeasurementUnit") && <span className="text-danger">{errors.get("secondaryCalculatedMeasurementUnit")}</span>
                                }
                            </Col>
                        </Row>
                }
            </Container>
        </>
    );
}